import {Command} from '@application/command/command';
import {renameProductionSchedulePathsToNewPositionInColoredYarnSets} from '@application/helper/production-schedule-builder';
import {updateProductionSchedulePathsOfColoredYarnSets} from '@application/helper/production-schedule-builder/update-production-schedule-paths-of-colored-yarn-sets';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionScheduleItemInPathGroup} from '@domain/production-schedule/production-schedule-item-in-path-group';
import {ProductionSchedulePath} from '@domain/production-schedule/production-schedule-path';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {OverviewListColoredYarnSetWithStartDent} from '@domain/textile-data/creel/overview-list-colored-yarn-set-with-start-dent';
import {ListDrawing} from '@presentation/components/drawing-list/list-drawing';
import {ListDrawingOrderLine} from '@presentation/components/drawing-list/list-drawing-order-line';
import {ParametersForMovingProductionSchedulePathToColoredYarnSet} from '@presentation/pages/texfab/production-schedule/add/plan/parameters-for-moving-production-schedule-path-to-colored-yarn-set';
import {ArrayUtils, AssertionUtils} from '@vdw/angular-component-library';
import {concat, each, isEqual, isNil, last} from 'lodash-es';
import {MoveProductionScheduleItemToProductionSchedulePathCommand} from './move-production-schedule-item-to-production-schedule-path-command';

export class MoveProductionSchedulePathToColoredYarnSetCommand implements Command<ParametersForMovingProductionSchedulePathToColoredYarnSet, ProductionSchedule> {
  public execute(commandArgs: ParametersForMovingProductionSchedulePathToColoredYarnSet): ProductionSchedule {
    let {productionSchedule} = commandArgs;
    const {listDrawings, startDentOfSourceColoredYarnSet, sourceProductionSchedulePathNumber, startDentOfDestinationColoredYarnSet, indexInProductionSchedulePathsOfColoredYarnSet, pathIndex} =
      commandArgs;

    const sourceProductionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet =
      productionSchedule.getProductionSchedulePathsOfColoredYarnSetAtStartDent(startDentOfSourceColoredYarnSet);
    const sourceProductionSchedulePath = sourceProductionSchedulePathsOfColoredYarnSet.getProductionSchedulePath(sourceProductionSchedulePathNumber);

    this.updateDrawingInfoOnPathGroups(sourceProductionSchedulePath, listDrawings);

    const destinationProductionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet =
      productionSchedule.getProductionSchedulePathsOfColoredYarnSetAtStartDent(startDentOfDestinationColoredYarnSet);
    const productionScheduleColoredYarnSet: OverviewListColoredYarnSetWithStartDent = productionSchedule.getProductionScheduleColoredYarnSetAtStartDent(
      destinationProductionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent
    );

    const totalWidthInDentsOfProductionSchedulePaths = ArrayUtils.sum(
      Array.from(destinationProductionSchedulePathsOfColoredYarnSet.productionSchedulePaths, (productionSchedulePath: ProductionSchedulePath) => productionSchedulePath.technicalWidthInDents)
    );

    const productionSchedulePathStartDent = isEqual(totalWidthInDentsOfProductionSchedulePaths, 0)
      ? productionScheduleColoredYarnSet.startDent
      : totalWidthInDentsOfProductionSchedulePaths + productionScheduleColoredYarnSet.startDent + 1;

    const newProductionSchedulePath = new ProductionSchedulePath('New Path', null, null, null, [], productionSchedulePathStartDent, null, null);

    if (isNil(indexInProductionSchedulePathsOfColoredYarnSet)) {
      destinationProductionSchedulePathsOfColoredYarnSet.productionSchedulePaths.push(newProductionSchedulePath);
    } else {
      if (AssertionUtils.isNullOrUndefined(pathIndex)) {
        destinationProductionSchedulePathsOfColoredYarnSet.productionSchedulePaths.splice(indexInProductionSchedulePathsOfColoredYarnSet, 0, newProductionSchedulePath);
      } else {
        destinationProductionSchedulePathsOfColoredYarnSet.productionSchedulePaths[pathIndex] = destinationProductionSchedulePathsOfColoredYarnSet.productionSchedulePaths.splice(
          indexInProductionSchedulePathsOfColoredYarnSet,
          1,
          sourceProductionSchedulePath
        )[0];
      }
    }

    let destinationProductionSchedulePath: ProductionSchedulePath;

    if (AssertionUtils.isNullOrUndefined(indexInProductionSchedulePathsOfColoredYarnSet)) {
      destinationProductionSchedulePath = last(destinationProductionSchedulePathsOfColoredYarnSet.productionSchedulePaths);
    } else {
      destinationProductionSchedulePath = destinationProductionSchedulePathsOfColoredYarnSet.productionSchedulePaths[indexInProductionSchedulePathsOfColoredYarnSet];
    }

    if (AssertionUtils.isNullOrUndefined(pathIndex)) {
      const pathGroups = concat(sourceProductionSchedulePath.pathGroups);

      each(pathGroups, (productionScheduleItemInPathGroup: ProductionScheduleItemInPathGroup, index: number) => {
        productionSchedule = new MoveProductionScheduleItemToProductionSchedulePathCommand().execute({
          productionSchedule,
          sourceProductionSchedulePathNumber: sourceProductionSchedulePath.pathNumber,
          startDentOfSourceColoredYarnSet: sourceProductionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent,
          destinationProductionSchedulePathNumber: destinationProductionSchedulePath.pathNumber,
          indexInDestinationProductionSchedulePath: index,
          startDentOfDestinationColoredYarnSet: destinationProductionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent,
          productionScheduleItemInPathGroup
        });
      });
    }

    renameProductionSchedulePathsToNewPositionInColoredYarnSets(productionSchedule);

    return updateProductionSchedulePathsOfColoredYarnSets(productionSchedule);
  }

  private updateDrawingInfoOnPathGroups(productionSchedulePath: ProductionSchedulePath, listDrawings: ListDrawing[]): void {
    productionSchedulePath.pathGroups.forEach((productionScheduleItemInPathGroup: ProductionScheduleItemInPathGroup) => {
      const listDrawingForProductionScheduleItems = listDrawings.filter((listDrawing: ListDrawing) => {
        let id = productionScheduleItemInPathGroup.drawing.image?.id;
        if (AssertionUtils.isNullOrUndefined(id)) {
          return false;
        }

        if (listDrawing instanceof ListDrawingOrderLine) {
          id = `${productionScheduleItemInPathGroup.orderLineId}_${id}`;
        }

        return listDrawing.id === id;
      });

      if (!AssertionUtils.isEmpty(listDrawingForProductionScheduleItems) && !AssertionUtils.isNullOrUndefined(listDrawingForProductionScheduleItems[0].drawing)) {
        productionScheduleItemInPathGroup.drawing = listDrawingForProductionScheduleItems[0].drawing;
      }
    });
  }
}
